import React, { useState, useEffect } from "react";
import { Card, Table, Button, Alert, Badge } from "react-bootstrap";
import api from "../services/api";
import { useLanguage } from "../i18n/LanguageContext";

interface Form106 {
  id: number;
  user_id: number;
  file_name: string;
  year: number;
  uploaded_at: string;
  can_download: boolean;
}

const EmployeeForm106: React.FC = () => {
  const { t } = useLanguage();
  const [forms, setForms] = useState<Form106[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      setLoading(true);
      const response = await api.get("/form106/my-forms");
      setForms(response.data.forms);
    } catch (err: any) {
      // 404 just means no forms yet — show empty state, not an error
      if (err.response?.status !== 404) {
        setError(err.response?.data?.error || t("failedToLoad"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (formId: number, fileName: string) => {
    try {
      const response = await api.get(`/form106/download/${formId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setError(t("downloadFailed"));
    }
  };

  const byYear = forms.reduce(
    (acc, form) => {
      if (!acc[form.year]) acc[form.year] = [];
      acc[form.year].push(form);
      return acc;
    },
    {} as Record<number, Form106[]>,
  );
  const years = Object.keys(byYear)
    .map(Number)
    .sort((a, b) => b - a);

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3 text-muted">{t("loading")}</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {forms.length === 0 ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center py-5">
            <div className="mb-3" style={{ fontSize: "48px" }}>
              📋
            </div>
            <h5 className="text-muted mb-2">{t("noForm106Available")}</h5>
            <p className="text-muted small px-3">{t("noForm106Message")}</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card className="shadow-sm border-0 mb-3">
            <Card.Body className="p-3 text-center">
              <div className="text-muted small mb-1">{t("totalForm106")}</div>
              <h3 className="mb-0 text-primary" style={{ fontWeight: 700 }}>
                {forms.length}
              </h3>
            </Card.Body>
          </Card>

          {years.map((year) => (
            <Card key={year} className="shadow-sm border-0 mb-3">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-2 px-3">
                <h5 className="mb-0" style={{ fontWeight: 600 }}>
                  {t("form106Title")} {year}
                </h5>
                <Badge bg="light" text="primary">
                  {byYear[year].length}
                </Badge>
              </Card.Header>
              <Card.Body className="p-0">
                {/* Mobile */}
                <div className="d-md-none">
                  {byYear[year].map((form) => (
                    <div key={form.id} className="p-3 border-bottom">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div style={{ fontWeight: 600 }}>{year}</div>
                          <div className="text-muted small">
                            {new Date(form.uploaded_at).toLocaleDateString()}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() =>
                            handleDownload(form.id, form.file_name)
                          }
                        >
                          📥 {t("download")}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop */}
                <div className="d-none d-md-block">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("year")}</th>
                        <th>{t("fileName")}</th>
                        <th>{t("uploadDate")}</th>
                        <th>{t("actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byYear[year].map((form) => (
                        <tr key={form.id}>
                          <td>
                            <strong>{form.year}</strong>
                          </td>
                          <td>
                            <small className="text-muted">
                              {form.file_name}
                            </small>
                          </td>
                          <td>
                            <small>
                              {new Date(form.uploaded_at).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() =>
                                handleDownload(form.id, form.file_name)
                              }
                            >
                              📥 {t("downloadPDF")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          ))}
        </>
      )}
    </>
  );
};

export default EmployeeForm106;
